import UPNG from '@pdf-lib/upng';
import logger from 'src/logger';
import AnimationsManager from 'src/sdk-components/AnimationsManager';
import { getAspectRatio } from 'src/utils';
import { disposeMesh } from 'src/utils/object3DHelpers';
import { getComposedTexture } from '../texture';
import { ComponentInteractionType, Inputs, Outputs, SceneComponent, LoadingState } from './types';

export class UPNGLoaderComponent extends SceneComponent {
  private root: any = null;

  private pivot: any = null;

  private model: any = null;

  private size: [number, number];

  private animationsManager: AnimationsManager | null;

  private composedTexture: any = null;

  public animate = false;

  inputs: Inputs = {
    url: null,
    width: null,
    height: null,
    enableHoverAnimation: false,
    animationPulse: false,
    animationUpDown: false,
    animationFlip: false,
  };

  outputs = {
    loadingState: LoadingState.Idle,
  } as Outputs;

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: true,
    [ComponentInteractionType.HOVER]: true,
    [ComponentInteractionType.DRAG]: false,
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();

    this.outputs.objectRoot = this.root;
    // setting the collider to add the click event
    this.outputs.collider = this.root;
    this.animationsManager = new AnimationsManager(THREE, this.root.parent);

    this.root.add(this.pivot);
    this.outputs.collider = this.root;
    getAspectRatio(this.inputs.url)
      .then(([w, h]) => {
        this.size = [w / 100, h / 100]; // scale down from pixels to meters
        this.loadModel();
      })
      .catch(() => {
        logger.error('Failed to init apng');
        this.outputs.loadingState = LoadingState.Error;
      });
  }

  onDestroy() {
    if (this.model) {
      disposeMesh(this.model);
      this.model = null;
    }
  }

  private loadModel = () => {
    const THREE = this.context.three;
    const fileLoader = new THREE.FileLoader();
    const [w, h] = this.size;
    const { width, height, url } = this.inputs;

    if (this.model) {
      disposeMesh(this.model);
      this.model = null;
    }

    if (!url) return;
    this.outputs.loadingState = LoadingState.Loading;

    fileLoader.responseType = 'arraybuffer';
    fileLoader.load(url, async (data: ArrayBuffer) => {
      const png = UPNG.decode(data as ArrayBuffer);

      const frames: {
        dims: { left: any; top: any; width: any; height: any };
        patch: any;
        blend: any;
        delay: any;
        disposalType: any;
      }[] = [];
      png.frames.forEach((frame: any) => {
        if (frame) {
          const dst = {
            dims: {
              left: frame.rect.x,
              top: frame.rect.y,
              width: frame.rect.width,
              height: frame.rect.height,
            },
            patch: frame.data,
            blend: frame.blend,
            delay: frame.delay,
            disposalType: frame.dispose,
          };
          frames.push(dst);
        }
      });

      const container = {
        downscale: false,
        width: png.width,
        height: png.height,
        frames,
      };

      const THREEComposedTexture = getComposedTexture(
        THREE,
        this.context.renderer.capabilities.maxTextureSize,
      );
      // @ts-ignore
      this.composedTexture = new THREEComposedTexture(container);
      this.composedTexture.encoding = THREE.LinearEncoding;

      const geometry = new THREE.PlaneBufferGeometry(width || w, height || h);
      const material = new THREE.MeshBasicMaterial({
        map: this.composedTexture,
        transparent: true,
        side: THREE.DoubleSide,
        alphaTest: 0.1,
      });
      this.model = new THREE.Mesh(geometry, material);

      this.pivot?.add(this.model);
      this.outputs.loadingState = LoadingState.Loaded;

      this.playAnimation();
    });
  };

  public playAnimation() {
    const THREE = this.context.three;

    if (!this.animationsManager) {
      this.animationsManager = new AnimationsManager(THREE, this.root.parent);
    }
    if (this.inputs.animationPulse) {
      this.animationsManager.pulse();
      this.animationsManager.play();
    } else if (this.inputs.animationUpDown) {
      this.animationsManager.bounceUpDown();
      this.animationsManager.play();
    } else if (this.inputs.animationFlip) {
      this.animationsManager.flip();
      this.animationsManager.play();
    }
  }

  toggleHoverAnimation(value: boolean) {
    if (this.animationsManager && this.inputs.enableHoverAnimation) {
      if (value) {
        if (this.animationsManager) {
          this.animationsManager.stop();
          this.animationsManager.outlinePulse();
          this.animationsManager.play();
        }
      } else {
        this.animationsManager.stop();

        if (this.inputs.animationPulse) {
          this.animationsManager.pulse();
          this.animationsManager.play();
        } else if (this.inputs.animationUpDown) {
          this.animationsManager.bounceUpDown();
          this.animationsManager.play();
        } else if (this.inputs.animationFlip) {
          this.animationsManager.flip();
          this.animationsManager.play();
        }
      }
    }
  }

  stopAnimation() {
    if (this.animationsManager) {
      this.animationsManager.stop();
      this.animationsManager = null;
    }
  }

  onTick(delta: number) {
    if (this.animate && this.composedTexture) {
      this.composedTexture.update(delta / 1000);
    }
    if (this.animate && this.animationsManager) {
      this.animationsManager.update(delta);
    }
  }
}

export const UPNGLoaderComponentType = 'mp.UPNGLoaderComponent';
export const makeUPNGLoaderComponent = (): any => new UPNGLoaderComponent();
